import DOMPurify from "dompurify";
import { useCMS } from "@/hooks/useCMS";
import { CmsSection } from "@/components/CmsSection";
import { Eyebrow, PillButton } from "@/components/motifs";

export const SECTION_TEMPLATES = [
  { template: "hero", name: "Hero banner", desc: "Eyebrow, big headline, intro and a button.", hint: "Set a background colour/image for impact.", seed: { eyebrow: "New", heading: "A bold headline here", subheading: "A short supporting sentence that explains the idea.", cta_label: "Learn more", cta_link: "#" } },
  { template: "cards3", name: "3-card row", desc: "A heading plus three editable cards.", hint: "Add cards as lines “Title | description”.", seed: { heading: "Three things to know", bullets: "First card | A short description of the first card.\nSecond card | A short description of the second card.\nThird card | A short description of the third card." } },
  { template: "quote", name: "Pull quote", desc: "A large quotation with attribution.", hint: "Sub-heading = quote, Heading = attribution.", seed: { subheading: "A memorable quote that captures the mission.", heading: "— Someone notable" } },
  { template: "stats", name: "Stats strip", desc: "A row of big numbers with labels.", hint: "Add stats as lines “Value | label”.", seed: { heading: "By the numbers", bullets: "50M+ | interactions\n500+ | communities\n2030 | the goal" } },
  { template: "cta", name: "CTA banner", desc: "A colourful call-to-action band.", hint: "Uses the accent colour as its background.", seed: { heading: "Ready to move?", subheading: "Join the movement today.", cta_label: "Get started", cta_link: "#", accent: "#DB1A5C" } },
  { template: "richtext", name: "Rich text", desc: "A heading plus a formatted rich-text body.", hint: "Edit the body with the visual editor.", seed: { heading: "About this", body: "<p>Write anything here using the visual editor — headings, bold, lists, links and images.</p>" } },
];

const lines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const pipe = (l) => { const [a, ...r] = l.split("|"); return [a?.trim() || "", r.join("|").trim()]; };

function Wrap({ children, className = "" }) {
  return <section className={`px-6 py-16 sm:py-24 ${className}`}><div className="mx-auto max-w-6xl">{children}</div></section>;
}

function TemplateBlock({ s }) {
  const accent = s.accent || "#2CACD4";
  if (s.template === "hero")
    return (
      <Wrap className="text-center">
        {s.eyebrow ? <Eyebrow>{s.eyebrow}</Eyebrow> : null}
        {s.heading ? <h2 className="mt-4 font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">{s.heading}</h2> : null}
        {s.subheading ? <p className="mx-auto mt-5 max-w-2xl text-base sm:text-lg opacity-90">{s.subheading}</p> : null}
        {s.cta_label ? <div className="mt-8 flex justify-center"><PillButton to={s.cta_link || "#"} testid={`tpl-cta-${s.key}`}>{s.cta_label}</PillButton></div> : null}
      </Wrap>
    );
  if (s.template === "cards3") {
    const cards = lines(s.bullets).map(pipe);
    return (
      <Wrap>
        {s.eyebrow ? <Eyebrow>{s.eyebrow}</Eyebrow> : null}
        {s.heading ? <h2 className="mt-3 font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl">{s.heading}</h2> : null}
        {s.subheading ? <p className="mt-3 max-w-2xl text-base opacity-80">{s.subheading}</p> : null}
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {cards.map(([title, body], i) => (
            <div key={i} className="rounded-3xl border border-border bg-card p-7 shadow-sm" style={{ borderTopColor: accent, borderTopWidth: 4 }} data-testid={`tpl-card-${s.key}-${i}`}>
              <p className="font-display font-black uppercase text-xl">{title}</p>
              {body ? <p className="mt-2 text-sm text-muted-foreground">{body}</p> : null}
            </div>
          ))}
        </div>
      </Wrap>
    );
  }
  if (s.template === "quote")
    return (
      <Wrap className="text-center">
        <blockquote className="mx-auto max-w-3xl font-display font-light italic text-2xl sm:text-3xl lg:text-4xl leading-snug">“{s.subheading}”</blockquote>
        {s.heading ? <p className="mt-6 font-bold uppercase tracking-wider text-sm opacity-80">{s.heading}</p> : null}
      </Wrap>
    );
  if (s.template === "stats") {
    const stats = lines(s.bullets).map(pipe);
    return (
      <Wrap>
        {s.heading ? <h2 className="font-display font-black uppercase text-3xl sm:text-4xl">{s.heading}</h2> : null}
        <div className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-4">
          {stats.map(([value, label], i) => (
            <div key={i} data-testid={`tpl-stat-${s.key}-${i}`}>
              <p className="font-display font-black text-4xl sm:text-5xl" style={{ color: accent }}>{value}</p>
              {label ? <p className="mt-1 text-sm uppercase tracking-wide opacity-70">{label}</p> : null}
            </div>
          ))}
        </div>
      </Wrap>
    );
  }
  if (s.template === "cta")
    return (
      <Wrap>
        <div className="rounded-[2rem] px-8 py-14 text-center text-white sm:px-16" style={{ backgroundColor: accent }}>
          {s.heading ? <h2 className="font-display font-black uppercase text-3xl sm:text-5xl">{s.heading}</h2> : null}
          {s.subheading ? <p className="mx-auto mt-3 max-w-xl text-base opacity-90 sm:text-lg">{s.subheading}</p> : null}
          {s.cta_label ? <div className="mt-7 flex justify-center"><PillButton to={s.cta_link || "#"} variant="dark" testid={`tpl-cta-${s.key}`}>{s.cta_label}</PillButton></div> : null}
        </div>
      </Wrap>
    );
  if (s.template === "richtext")
    return (
      <Wrap>
        {s.heading ? <h2 className="mb-5 font-display font-black uppercase text-3xl sm:text-4xl">{s.heading}</h2> : null}
        <div className="article-content" data-testid={`tpl-richtext-${s.key}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(s.body || "") }} />
      </Wrap>
    );
  return null;
}

export function TemplateSections({ page }) {
  const { data } = useCMS();
  const secs = (data.sections || []).filter((s) => s.page === page && s.template);
  if (!secs.length) return null;
  return (
    <>
      {secs.map((s) => (
        <CmsSection key={s.id || s.key} sk={s.key}>
          <TemplateBlock s={s} />
        </CmsSection>
      ))}
    </>
  );
}
